import { useEffect, useRef } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { CheckoutPageShell } from '@/components/ecommerce/CheckoutSteps'
import { checkoutStorage } from '@/lib/ecommerce/checkoutStorage'
import { useCart } from '@/hooks/useCart'
import { useAuth } from '@/hooks/useAuth'
import { useAvailabilityGuard } from '@/hooks/useAvailabilityGuard'
import { evictSoldOrRedirect } from '@/lib/ecommerce/availability'
import { createContactAddress, updateContactProperties } from '@/lib/ecommerce/api'
import { isPickupOption } from '@/lib/ecommerce/shippingOptions'
import type { CheckoutPagePayload } from '@/types/ecommerce'

/* Guest checkout deferred-address creation.

   For unregistered buyers, /checkout/shipping and /checkout/billing
   intentionally do NOT create crm_address records — they only save
   fields to sessionStorage. This function runs once, right before
   the order actually submits, to:

     1. Create the single Shipping crm_address record tied to the
        guest's order contact (or reuse one already created earlier
        in this submit cycle — covers Stripe-failure retries).
     2. Create the single Billing crm_address record IF the buyer
        chose different billing details. Otherwise the billing_address_id
        equals the shipping_address_id so the order points at the
        same row for both.
     3. Update sessionStorage's shipping/billing entries with the new
        shipping_address_id / billing_address_id so applyCheckoutMirror's
        next call writes them into the form's hidden inputs.
     4. Update the buyer's CRM contact with
        checkout_shipping_address_uuid / checkout_billing_address_uuid
        / checkout_shipping_instructions so the addon's
        callback_update_records can resolve them post-order.

   The product requirement that drives this: a guest who clicks
   Continue on /checkout/shipping then back-navigates and clicks
   Continue again should end the checkout with exactly ONE shipping
   crm_address record and ONE billing crm_address record per order —
   regardless of how many back-and-forth cycles they did. Deferring
   creation to here guarantees that, and also keeps CRM clean from
   abandoned-cart orphan addresses. */
async function finalizeGuestAddresses(): Promise<void> {
  const contact = checkoutStorage.readContact()
  const shipping = checkoutStorage.readShipping()
  const billing = checkoutStorage.readBilling()

  const orderContactUuid = contact?.resolved_contact_uuid
  if (!orderContactUuid || !shipping) {
    // Without a resolved guest contact UUID or shipping fields the
    // order can't be attached to an address. Bail out — the addon's
    // server-side form validation will surface a friendlier error.
    return
  }

  // UAT-36: pickup options collect no delivery address. Don't create a
  // crm_address (it would be an orphan with empty fields); just persist
  // the chosen option + any instructions on the contact.
  if (isPickupOption(shipping.shipping_option)) {
    try {
      await updateContactProperties({
        uuid: orderContactUuid,
        checkout_shipping_option: shipping.shipping_option,
        checkout_shipping_instructions: shipping.shipping_instructions ?? '',
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('updateContactProperties (pickup finalize) failed:', e)
    }
    return
  }

  let shippingAddressId = ''
  let shippingAddressUuid = ''
  const existingShipping = checkoutStorage.readCreatedAddress('Shipping')
  if (existingShipping) {
    // Already created on a previous submit attempt this session (e.g.
    // the buyer hit a Stripe-declined error and is retrying). Reuse.
    shippingAddressId = existingShipping.id
    shippingAddressUuid = existingShipping.uuid
  } else {
    const created = await createContactAddress({
      related_uuid: orderContactUuid,
      related_type: 'modules/insites_core/crm_contact',
      address_label: 'Shipping',
      address_1: shipping.shipping_address_1,
      address_2: shipping.shipping_address_2,
      suburb: shipping.shipping_suburb,
      state: shipping.shipping_state,
      postcode: shipping.shipping_postcode,
      country: shipping.shipping_country,
      is_default_shipping: true,
    })
    shippingAddressId = created?.id != null ? String(created.id) : ''
    shippingAddressUuid = (created?.uuid as string | undefined) ?? ''
    if (shippingAddressId && shippingAddressUuid) {
      checkoutStorage.saveCreatedAddress('Shipping', {
        id: shippingAddressId,
        uuid: shippingAddressUuid,
        address_label: 'Shipping',
        address_1: shipping.shipping_address_1,
        address_2: shipping.shipping_address_2,
        suburb: shipping.shipping_suburb,
        state: shipping.shipping_state,
        postcode: shipping.shipping_postcode,
        country: shipping.shipping_country,
      })
    }
  }

  // Echo the new ID into the shipping mirror so applyCheckoutMirror's
  // next pass writes it into the form's hidden shipping_address_id.
  if (shippingAddressId) {
    checkoutStorage.saveShipping({
      ...shipping,
      shipping_address_id: shippingAddressId,
    })
  }

  // Default billing to the shipping record (same_as_shipping path).
  let billingAddressId = shippingAddressId
  let billingAddressUuid = shippingAddressUuid

  if (billing && billing.billing_same_with_shipping === false) {
    const existingBilling = checkoutStorage.readCreatedAddress('Billing')
    if (existingBilling) {
      billingAddressId = existingBilling.id
      billingAddressUuid = existingBilling.uuid
    } else {
      const createdB = await createContactAddress({
        related_uuid: orderContactUuid,
        related_type: 'modules/insites_core/crm_contact',
        address_label: 'Billing',
        address_1: billing.billing_address_1,
        address_2: billing.billing_address_2,
        suburb: billing.billing_suburb,
        state: billing.billing_state,
        postcode: billing.billing_postcode,
        country: billing.billing_country,
        is_default_billing: true,
      })
      billingAddressId = createdB?.id != null ? String(createdB.id) : ''
      billingAddressUuid = (createdB?.uuid as string | undefined) ?? ''
      if (billingAddressId && billingAddressUuid) {
        checkoutStorage.saveCreatedAddress('Billing', {
          id: billingAddressId,
          uuid: billingAddressUuid,
          address_label: 'Billing',
          address_1: billing.billing_address_1,
          address_2: billing.billing_address_2,
          suburb: billing.billing_suburb,
          state: billing.billing_state,
          postcode: billing.billing_postcode,
          country: billing.billing_country,
        })
      }
    }
  }

  if (billing && billingAddressId) {
    checkoutStorage.saveBilling({
      ...billing,
      billing_address_id: billingAddressId,
    })
  }

  // Tag the buyer's CRM contact so the addon's order callback can
  // resolve the addresses by checkout_*_address_uuid in addition to
  // by related_uuid+label. Best-effort — failures don't block the
  // order; the order callback's related_uuid lookup still works.
  if (shippingAddressUuid || billingAddressUuid) {
    try {
      await updateContactProperties({
        uuid: orderContactUuid,
        checkout_shipping_address_uuid: shippingAddressUuid || undefined,
        checkout_billing_address_uuid: billingAddressUuid || undefined,
        checkout_shipping_instructions: shipping.shipping_instructions ?? '',
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('updateContactProperties (finalize) failed:', e)
    }
  }
}

/*
  Checkout Step 4 — Payment (Teamwork 26100598).

  We follow the addon's reference template and only swap the visual
  shell. The Liquid wrapper renders the addon's checkout_payment form
  server-side via include_form and passes the resulting HTML in
  payload.payment_form_html. We inject that HTML into the main column
  via dangerouslySetInnerHTML and rehydrate any inline <script> tags it
  contains (Stripe.js loader, Stripe Elements bootstrap, etc.) so the
  form's onsubmit handler — Checkout.events.paymentFormSubmit, defined
  in the addon's checkout.min.js loaded by the Liquid wrapper — runs
  exactly the way it would inside the addon's checkout_layout.

  The addon's form takes care of:
    • Stripe Elements card capture + tokenisation
    • POST to /api/orders → creates the Order record
    • callback_init → callback_update_records → Stripe charge,
      payment record, mark_product_sold, email workflows
    • redirect_to /confirmation?id=<uuid> on success, or back to
      /checkout/payment?status=payment-error&type=<code> on failure

  We surface the failure case inline at the top of the form by reading
  payment_status / payment_error_type from the query string.
*/

async function rehydrateScripts(container: HTMLElement) {
  // Browsers do not execute <script> tags that are inserted via
  // innerHTML. Walk the captured form HTML and re-create each script
  // node so it actually runs. External scripts load asynchronously, so
  // we await each one's load event before processing the next tag —
  // this preserves the document-order semantics the addon's form
  // assumes (e.g. `<script src="https://js.stripe.com/v3/">` followed
  // by an inline `Stripe(pk_...)` call that needs the global to exist).
  const scripts = Array.from(container.querySelectorAll('script'))
  for (const old of scripts) {
    const next = document.createElement('script')
    for (const attr of Array.from(old.attributes)) {
      next.setAttribute(attr.name, attr.value)
    }
    if (old.textContent) next.textContent = old.textContent

    if (next.src) {
      const loaded = new Promise<void>((resolve) => {
        next.onload = () => resolve()
        next.onerror = () => resolve()
      })
      old.parentNode?.replaceChild(next, old)
      await loaded
    } else {
      // Inline scripts execute synchronously upon insertion.
      old.parentNode?.replaceChild(next, old)
    }
  }
}

/*
  Patch the addon form's hidden inputs with the canonical buyer entries
  the previous checkout steps stored in sessionStorage (via
  checkoutStorage).

  PlatformOS auto-renders one hidden input per declared property in the
  form's `fields:` block, with name `form[properties_attributes][<prop>]`.
  The selector below targets that exact suffix. If a hidden input
  doesn't exist (because the form's schema doesn't declare that
  property) we APPEND a new one — that way every captured value is
  guaranteed to land on the order even if the schema drifts.

  This function is called twice: once after rehydration (so DevTools
  inspection shows the right values pre-submit) and once again from a
  capture-phase submit listener (so even if the addon's checkout.js
  resets values during init, we re-apply them right before the form
  serialises). Both invocations are idempotent.
*/
function applyCheckoutMirror(host: HTMLElement): {
  applied: number
  missing: string[]
} {
  const contact = checkoutStorage.readContact()
  const shipping = checkoutStorage.readShipping()
  const billing = checkoutStorage.readBilling()

  const form =
    host.tagName === 'FORM'
      ? (host as HTMLFormElement)
      : host.querySelector<HTMLFormElement>('form#checkout-form') ?? host

  let applied = 0
  const missing: string[] = []

  const setByPropertyName = (propertyName: string, value: string) => {
    if (value == null) return
    // PlatformOS forms emit hidden inputs at
    // `form[properties_attributes][<prop>]`. Match by suffix to avoid
    // hardcoding the form's wrapping name (which can vary).
    const selector = `input[name$="[${propertyName}]"], input[name="${propertyName}"]`
    const inputs = form.querySelectorAll<HTMLInputElement>(selector)
    if (inputs.length === 0) {
      // Fall back to creating a new hidden input. Only the LAST input
      // with a given name wins on the server side, so appending is safe.
      const input = document.createElement('input')
      input.type = 'hidden'
      // We don't know the exact prefix the platform used, but since
      // properties_attributes is the canonical pattern, write directly
      // to that. Any auto-rendered input with the same name would have
      // matched above so this branch only fires for genuinely missing
      // schema entries.
      input.name = `form[properties_attributes][${propertyName}]`
      input.value = value
      form.appendChild(input)
      missing.push(propertyName)
      applied += 1
      return
    }
    inputs.forEach((input) => {
      input.value = value
    })
    applied += 1
  }

  if (contact) {
    setByPropertyName('order_company_name', contact.contact_company_name)
    setByPropertyName('order_contact_first_name', contact.contact_first_name)
    setByPropertyName('order_contact_last_name', contact.contact_last_name)
    setByPropertyName('order_contact_email', contact.contact_email)
    setByPropertyName('order_contact_phone_number', contact.contact_phone_number)
    setByPropertyName('order_contact_phone_country_code', contact.contact_phone_country_code)

    // Stripe form's hidden inputs (used by stripe-element.js to
    // build the Add-card payload — see stripe_element.liquid).
    // These are populated server-side from context.session at render
    // time, which is empty for guests on this instance. Patch them
    // here so the "Add" button doesn't fail with "Email is required"
    // for guest checkouts. Note: these are document-scoped (not
    // necessarily inside form#checkout-form) — stripe_element.liquid
    // renders them at the partial root, and the addon's stripe-element.js
    // looks them up via document.getElementById.
    const cardEmail = document.getElementById('card_email') as HTMLInputElement | null
    const cardFirst = document.getElementById('card_first_name') as HTMLInputElement | null
    const cardLast = document.getElementById('card_last_name') as HTMLInputElement | null
    if (cardEmail) cardEmail.value = contact.contact_email ?? ''
    if (cardFirst) cardFirst.value = contact.contact_first_name ?? ''
    if (cardLast) cardLast.value = contact.contact_last_name ?? ''
  }

  if (shipping) {
    // UAT-36: the chosen shipping option is the single client signal the
    // server reads to derive the $50 fee + freight fields + status
    // (callback_init + callback_update_records). Declared as a form
    // property so this patches an auto-rendered hidden input that binds
    // reliably for guests and authenticated buyers alike.
    setByPropertyName('shipping_option', shipping.shipping_option || '')
    setByPropertyName('shipping_company_name', shipping.shipping_company_name)
    setByPropertyName('shipping_contact_first_name', shipping.shipping_contact_first_name)
    setByPropertyName('shipping_contact_last_name', shipping.shipping_contact_last_name)
    setByPropertyName('shipping_contact_email', shipping.shipping_contact_email)
    setByPropertyName('shipping_contact_phone_number', shipping.shipping_contact_phone_number)
    setByPropertyName(
      'shipping_contact_phone_country_code',
      shipping.shipping_contact_phone_country_code,
    )
    setByPropertyName('shipping_address_id', shipping.shipping_address_id)
    setByPropertyName('shipping_address_1', shipping.shipping_address_1)
    setByPropertyName('shipping_address_2', shipping.shipping_address_2)
    setByPropertyName('shipping_address_3', shipping.shipping_address_3)
    setByPropertyName('shipping_suburb', shipping.shipping_suburb)
    setByPropertyName('shipping_state', shipping.shipping_state)
    setByPropertyName('shipping_postcode', shipping.shipping_postcode)
    setByPropertyName('shipping_country', shipping.shipping_country)
    setByPropertyName('shipping_instructions', shipping.shipping_instructions)
  }

  if (billing) {
    setByPropertyName('billing_company_name', billing.billing_company_name)
    setByPropertyName('billing_contact_first_name', billing.billing_contact_first_name)
    setByPropertyName('billing_contact_last_name', billing.billing_contact_last_name)
    setByPropertyName('billing_contact_email', billing.billing_contact_email)
    setByPropertyName('billing_contact_phone_number', billing.billing_contact_phone_number)
    setByPropertyName(
      'billing_contact_phone_country_code',
      billing.billing_contact_phone_country_code,
    )
    setByPropertyName('billing_address_id', billing.billing_address_id)
    setByPropertyName('billing_address_1', billing.billing_address_1)
    setByPropertyName('billing_address_2', billing.billing_address_2)
    setByPropertyName('billing_address_3', billing.billing_address_3)
    setByPropertyName('billing_suburb', billing.billing_suburb)
    setByPropertyName('billing_state', billing.billing_state)
    setByPropertyName('billing_postcode', billing.billing_postcode)
    setByPropertyName('billing_country', billing.billing_country)
  }

  return { applied, missing }
}

function PaymentErrorBanner({ status, type }: { status?: string | null; type?: string | null }) {
  if (status !== 'payment-error') return null
  // Map common Stripe error codes to friendlier copy. Anything we don't
  // recognise falls through to the raw code so the buyer can quote it
  // to support if needed.
  const message =
    {
      card_declined: 'Your card was declined. Please try a different card or contact your bank.',
      generic_decline: 'Your card was declined. Please try a different card or contact your bank.',
      insufficient_funds: 'Your card was declined for insufficient funds. Please try a different card.',
      expired_card: 'Your card has expired. Please use a different card.',
      incorrect_cvc: 'The CVC entered is incorrect. Please re-enter your card details.',
      incorrect_number: 'The card number is incorrect. Please re-enter your card details.',
      processing_error: 'There was a problem processing your card. Please try again.',
    }[type ?? ''] ?? `We could not process your payment${type ? ` (${type})` : ''}. Please try again or use a different card.`

  return (
    <div role="alert" className="mig-payment-error-banner">
      <strong>Payment failed.</strong> {message}
    </div>
  )
}

export default function CheckoutPayment() {
  const payload = usePageData<CheckoutPagePayload>() ?? {
    is_authenticated: false,
    user_uuid: null,
    session_id: null,
    contact_email: null,
  }
  // Mount-time eviction. If anything in the cart has been Sold by
  // another buyer the helper deletes the cart_item, stashes a
  // toast payload, and redirects to /cart — the buyer never sees
  // the rendered payment form.
  useAvailabilityGuard()
  const { items } = useCart()
  const { contactUuid } = useAuth()
  const formMountRef = useRef<HTMLDivElement | null>(null)
  const rehydratedRef = useRef(false)
  // Always-fresh refs so the submit wrapper (defined once on
  // rehydrate) can read the latest cart and contactUuid at click
  // time, not the snapshot from when the wrapper was installed.
  const itemsRef = useRef(items)
  const contactUuidRef = useRef(contactUuid)
  useEffect(() => { itemsRef.current = items }, [items])
  useEffect(() => { contactUuidRef.current = contactUuid }, [contactUuid])

  const formHtml = payload.payment_form_html ?? ''

  useEffect(() => {
    if (formMountRef.current && formHtml && !rehydratedRef.current) {
      // Rehydrate scripts after the addon form HTML lands in the DOM —
      // by the time this effect fires, technical_foot has loaded the
      // React bundle and the deferred checkout.min.js + stripe.js
      // tags from the Liquid wrapper.
      //
      // Guard against React StrictMode's double-invocation of effects
      // (mount → cleanup → mount in dev). Rehydrating scripts twice
      // re-runs `let card = ...` in stripe-element.js (throws), and
      // re-mounts Stripe Elements which produces two iframes inside
      // #card-element — only one of which has the typed card details,
      // so stripe.createToken(card) operates on the wrong one and
      // hangs the Add button forever.
      rehydratedRef.current = true
      rehydrateScripts(formMountRef.current).then(() => {
        const host = formMountRef.current
        if (!host) return

        // First pass — populate the auto-rendered hidden inputs so the
        // values are visible in DevTools and any pre-submit validation
        // sees them.
        const initial = applyCheckoutMirror(host)
        if (typeof window !== 'undefined' && (window as { console?: Console }).console) {
          // eslint-disable-next-line no-console
          console.log(
            `[mig-checkout-mirror] post-rehydrate applied=${initial.applied} appended=${initial.missing.length}`,
            initial.missing.length ? initial.missing : '',
          )
        }

        // Capture-phase submit gate. This is the *authoritative*
        // race-safe gate for the User-A-vs-User-B sold-out edge case
        // AND for IIA-admin-disabled products (api/cart/check-availability).
        //
        // Why capture-phase here and not a wrap on Checkout.events.paymentFormSubmit:
        //   - The form's `html-onsubmit` calls Checkout.events.paymentFormSubmit
        //     which the addon's checkout.min.js installs ~200ms after the
        //     bundle loads.
        //   - The previous implementation polled for that global for ≤1s and
        //     installed a function wrap. On slow connections or when the
        //     addon JS arrives late, the wrap never installed and the
        //     buyer could complete a charge for a product that had since
        //     been marked Sold or Disabled.
        //   - A capture-phase listener registered directly on the form
        //     fires for EVERY submit attempt, regardless of when (or
        //     whether) the addon globals exist. That makes the gate
        //     deterministic.
        //
        // The flag-based two-pass dance:
        //   1. First submit attempt arrives → flag is false →
        //      preventDefault + stopImmediatePropagation (which also
        //      blocks the onsubmit attribute from firing, so the addon's
        //      Stripe tokenisation never starts). Run the availability
        //      check async. If anything is unavailable, the helper
        //      deletes the cart_items, writes the toast payload to
        //      sessionStorage, and navigates to /cart. If everything is
        //      ok, set the flag and call form.requestSubmit() to fire a
        //      fresh submit event.
        //   2. Second pass → flag is true → re-apply the mirror, reset
        //      the flag (so a Stripe failure + user retry re-checks),
        //      and let the event propagate. The onsubmit attribute then
        //      runs the addon's paymentFormSubmit → Stripe → form.submit().
        const form = host.querySelector<HTMLFormElement>('form#checkout-form')
        if (form) {
          let availabilityPassed = false

          form.addEventListener(
            'submit',
            async (event) => {
              // Always re-apply the mirror so the about-to-submit form
              // carries the canonical values from sessionStorage.
              const result = applyCheckoutMirror(form)
              // eslint-disable-next-line no-console
              console.log(
                `[mig-checkout-mirror] pre-submit applied=${result.applied} appended=${result.missing.length}`,
              )

              if (availabilityPassed) {
                // Resubmitted from below — reset the flag so a later
                // attempt (e.g. after a Stripe declined card retry)
                // re-checks availability, then let the event continue
                // to the onsubmit attribute.
                availabilityPassed = false
                return
              }

              // First pass — block until the availability check resolves.
              event.preventDefault()
              event.stopImmediatePropagation()

              // MIG: prevent the Stripe "missing" charge. For a guest's
              // brand-new card the payment token only lands in #stripe-card
              // AFTER the "Add" round-trip finishes, but the addon reveals
              // the Purchase button while it's still polling for the token
              // (it sets a spinner but does NOT disable the button). Clicking
              // in that window submits with an empty token, so Stripe rejects
              // the charge with error code "missing"
              // (/checkout/payment?status=payment-error&type=missing). Wait
              // briefly for the token to arrive; the common race resolves in
              // ~1-2s so the order then completes with no buyer action. Only
              // if it never lands do we keep the buyer here with a clear
              // message instead of firing a doomed charge. Saved-card flows
              // already carry a token, so this is a no-op for them.
              const tokenEl = document.getElementById('stripe-card') as
                | (HTMLElement & { value?: string })
                | null
              const hasToken = () =>
                (tokenEl?.value ?? tokenEl?.getAttribute('value') ?? '').trim() !== ''
              if (!hasToken()) {
                const tokenBtn = document.getElementById('checkout-submit-btn') as
                  | { loading?: boolean }
                  | null
                if (tokenBtn && 'loading' in tokenBtn) tokenBtn.loading = true
                const tokenReady = await new Promise<boolean>((resolve) => {
                  let tries = 0
                  const iv = window.setInterval(() => {
                    tries += 1
                    if (hasToken()) {
                      window.clearInterval(iv)
                      resolve(true)
                    } else if (tries >= 40) {
                      // ~8s (40 × 200ms)
                      window.clearInterval(iv)
                      resolve(false)
                    }
                  }, 200)
                })
                if (tokenBtn && 'loading' in tokenBtn) tokenBtn.loading = false
                if (!tokenReady) {
                  const Notyf = (
                    window as unknown as {
                      Notyf?: new (o?: unknown) => { error: (m: string) => void }
                    }
                  ).Notyf
                  if (Notyf) {
                    new Notyf({ duration: 6000 }).error(
                      'Your card is still being added. Please wait a moment and click Purchase order again.',
                    )
                  }
                  return
                }
              }

              let blocked = false
              try {
                const res = await evictSoldOrRedirect({
                  items: itemsRef.current,
                  contactUuid: contactUuidRef.current,
                })
                if (res.removed > 0) {
                  blocked = true
                  // Reset the addon's spinner so the buyer sees the
                  // button restored to "Purchase order" before the
                  // /cart redirect navigates.
                  const submitBtn = document.getElementById(
                    'checkout-submit-btn',
                  ) as { loading?: boolean } | null
                  if (submitBtn && 'loading' in submitBtn) {
                    submitBtn.loading = false
                  }
                }
              } catch {
                // Network failure shouldn't block legitimate payments.
                // Fall through to the resubmit branch — the order
                // callback's mark_product_sold refuses to flip an
                // already-Sold product, and a re-check on the next
                // page mount will catch it.
              }

              if (blocked) return

              // GUEST DEFERRED ADDRESS CREATION: steps 2 and 3 skip
              // the crm_address create/update calls for unregistered
              // buyers to prevent duplicate-on-back-nav. Here, after
              // availability is OK and right before we let the order
              // actually submit, create the single shipping (and
              // billing, if different) record per the buyer's
              // sessionStorage entries. Authenticated buyers already
              // have records from steps 2/3 — skip.
              if (!payload.is_authenticated) {
                try {
                  await finalizeGuestAddresses()
                  // Re-apply the mirror so the form's hidden inputs
                  // pick up the new shipping_address_id /
                  // billing_address_id we just wrote into
                  // sessionStorage.
                  applyCheckoutMirror(form)
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.error('Guest address finalization failed:', e)
                  const submitBtn = document.getElementById(
                    'checkout-submit-btn',
                  ) as { loading?: boolean } | null
                  if (submitBtn && 'loading' in submitBtn) {
                    submitBtn.loading = false
                  }
                  // Stay on this page; buyer can retry. The order
                  // hasn't been created yet, so there's nothing to
                  // unwind.
                  return
                }
              }

              // Availability passed — fire submit again, this time
              // letting it through to the addon's onsubmit handler.
              availabilityPassed = true
              form.requestSubmit()
            },
            { capture: true },
          )
        }
      })
    }

  }, [formHtml])

  return (
    <CheckoutPageShell title="Checkout" active="payment" backHref="/checkout/billing">
      <div className="mig-payment-form-host">
        <PaymentErrorBanner status={payload.payment_status} type={payload.payment_error_type} />

        {formHtml ? (
          <div
            ref={formMountRef}
            // The form HTML comes from the addon's include_form output,
            // captured server-side; it carries CSRF + form_id + page_id
            // hidden inputs that PlatformOS populates per request, so
            // injecting it here does not bypass any security checks.
            dangerouslySetInnerHTML={{ __html: formHtml }}
          />
        ) : (
          <p className="mig-payment-form-fallback">
            Payment form failed to load. Please refresh the page or contact support.
          </p>
        )}
      </div>
    </CheckoutPageShell>
  )
}
